/*
================================================================================
File: src/pages/Admin.js
Description: Admin panel for user management with full functionality.
             Includes Big Dog/Non-Big Dog filtering.
================================================================================
*/
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useAdaptiveMMKV } from "../hooks/useAdaptiveMMKV";
import Select from "react-tailwindcss-select";
import instance from "../api/axios";
import { storage } from "../App";
import PageContent from "../components/common/PageContent";
import Modal from "../components/common/Modal";
import { SelectAllUserEmails } from "../components/common/FormGroup";
import { token } from "../constants/options";

// Reusable StatCard component (imported from Dashboard pattern)
const StatCard = ({ title, value, icon, accent }) => (
  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
        {title}
      </span>
      {icon && <div className={`p-2 rounded-lg ${accent}`}>{icon}</div>}
    </div>
    <p className="text-3xl font-bold text-gray-800 dark:text-gray-100">
      {value}
    </p>
  </div>
);

const Admin = ({ user }) => {
  const [users] = useAdaptiveMMKV("user.Admin.All", storage);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // NEW: State for filter type
  const [filterType, setFilterType] = useState("All");

  const [menuOpenForUser, setMenuOpenForUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({});
  const menuRef = useRef([]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutside = menuRef.current.every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutside) {
        setMenuOpenForUser(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // State for modal forms
  const [skipTraceCredits, setSkipTraceCredits] = useState(0);
  const [autoQueryCredits, setAutoQueryCredits] = useState(0);
  const [selectedID, setSelectedID] = useState(0);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [notimessage, setNotimessage] = useState("");
  const [notigroup, setNotigroup] = useState(null);
  const [notiurl, setNotiurl] = useState("");

  const fetchUsers = useCallback(async () => {
    const formData = new FormData();
    formData.append(
      "token",
      token(user.app_id, user.app_email, user.app_api_key)
    );
    formData.append("id", user.app_id);
    try {
      const response = await instance.post("/admin/getusers.php", formData);
      if (Array.isArray(response.data)) {
        const sortedData = [...response.data].sort((a, b) =>
          String(a.name).localeCompare(String(b.name))
        );
        storage.set("user.Admin.All", JSON.stringify(sortedData));
        setError(null);
      } else {
        setError("Failed to load user data.");
      }
    } catch (e) {
      if (e.response) {
        setError(
          "Error fetching user data: " +
            (e.response?.data?.message || e.message || "Unknown error")
        );
        console.error(
          "Error fetching user data: ",
          e.response?.data?.message || e.message
        );
      } else if (e.request) {
        setError(
          "Error: No response received from server. Please refresh and try again."
        );
        console.error("Error: ", e.request);
      } else {
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    }
  }, [user.app_id, user.app_email, user.app_api_key]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleModalAction = (action, selectedUserData) => {
    setModalContent({
      action,
      user: selectedUserData,
      title: getModalTitle(action, selectedUserData),
    });
    if (action === "edit_credits") {
      setSkipTraceCredits(selectedUserData.skip_trace_credits);
      setAutoQueryCredits(selectedUserData.query_credits);
      setSelectedID(selectedUserData.id);
    }
    setIsModalOpen(true);
    setMenuOpenForUser(null);
  };

  const getModalTitle = (action, u) => {
    switch (action) {
      case "ban":
        return `Ban User: ${u.name}`;
      case "edit_credits":
        return `Edit Credits for ${u.email}`;
      case "mass_notif":
        return "Mass Mobile Notification";
      case "sync_partner":
        return `Sync Partner for ${u.email}`;
      case "sync_password":
        return `Sync Password for ${u.name}`;
      default:
        return "Admin Action";
    }
  };

  const getModalIconAndColor = (action) => {
    switch (action) {
      case "ban":
        return {
          icon: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 2.526a6 6 0 008.367 8.364z" clipRule="evenodd" />
            </svg>
          ),
          color: "red",
        };
      case "edit_credits":
        return {
          icon: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
          ),
          color: "blue",
        };
      case "mass_notif":
        return {
          icon: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2a6 6 0 100 12A6 6 0 0010 2zM0 10a2 2 0 114 0 2 2 0 01-4 0zm16-2a2 2 0 100 4 2 2 0 000-4z" />
            </svg>
          ),
          color: "blue",
        };
      case "sync_partner":
      case "sync_password":
        return {
          icon: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a1 1 0 001 1h12a1 1 0 001-1V6a2 2 0 00-2-2H4zm12 12H4a2 2 0 01-2-2v-4a1 1 0 00-1-1H.5a1 1 0 001 1v4a4 4 0 004 4h12a1 1 0 001-1h-.5a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
          ),
          color: "blue",
        };
      default:
        return {
          icon: null,
          color: "blue",
        };
    }
  };

  const handleFormAction = async (e, action) => {
    e.preventDefault();
    setActionLoading(true);
    const formData = new FormData();
    formData.append(
      "token",
      token(user.app_id, user.app_email, user.app_api_key)
    );
    let url = "";

    switch (action) {
      case "edit_credits":
        formData.append("guest", selectedID);
        formData.append("owner", user.app_id);
        formData.append("skip_trace_credits", skipTraceCredits);
        formData.append("query_credits", autoQueryCredits);
        url = "/admin/edit_credits.php";
        break;
      case "mass_notif":
        formData.append("id", user.app_id);
        formData.append("web", notiurl);
        formData.append("type", notigroup.value);
        formData.append("enter-message", notimessage);
        url = "/admin/sendMessage.php";
        break;
      case "sync_partner":
        formData.append("email1", selectedPartner.value);
        formData.append("id", user.app_id);
        formData.append("email2", modalContent.user.email);
        url = "/admin/IFSSync.php";
        break;
      case "sync_password":
        formData.append("uid", modalContent.user.id);
        formData.append("id", user.app_id);
        url = "/admin/syncIFS.php";
        break;
      case "ban":
        formData.append("uid", modalContent.user.id);
        formData.append("id", user.app_id);
        formData.append("ban", modalContent.active ? "0" : "1");
        url = "/admin/banID.php";
        break;
      default:
        setActionLoading(false);
        return;
    }

    try {
      await instance.post(url, formData);
      setError(null);
      // Refresh users after successful action
      await fetchUsers().catch((e) => {
        console.error("Error refreshing users after action:", e);
      });
    } catch (e) {
      if (e.response) {
        const errorMsg =
          e.response?.data?.message || e.message || "Unknown error";
        setError(`Error with action ${action}: ` + errorMsg);
        console.error(`Error with action ${action}: `, errorMsg);
      } else if (e.request) {
        setError(
          "Error: No response received from server. Please refresh and try again."
        );
        console.error("Error: ", e.request);
      } else {
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      setActionLoading(false);
      setIsModalOpen(false);
    }
  };

  // UPDATED: Filter logic to include Big Dogs / Non-Big Dogs
  const filteredUsers = useMemo(() => {
    if (!users) return [];

    return users.filter((u) => {
      // 1. Text Search Check
      const matchesSearch = Object.values(u).some((val) =>
        String(val).toLowerCase().includes(searchTerm.toLowerCase())
      );

      // 2. Type Filter Check
      let matchesType = true;
      const keapId = String(u.bigdog || ""); // Safely handle null/undefined

      if (filterType === "Big Dogs") {
        // Big Dogs do NOT have "-x-"
        matchesType = !keapId.includes("-x-");
      } else if (filterType === "Non-Big Dogs") {
        // Non-Big Dogs DO have "-x-"
        matchesType = keapId.includes("-x-");
      }
      // If "All", matchesType remains true

      return matchesSearch && matchesType;
    });
  }, [users, searchTerm, filterType]);

  const currentUsers = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredUsers, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Compute admin stats
  const adminStats = useMemo(() => {
    if (!users) return { total: 0, active: 0, bigDog: 0, banned: 0 };
    return {
      total: users.length,
      active: users.filter((u) => u.active).length,
      bigDog: users.filter((u) => !String(u.bigdog || "").includes("-x-"))
        .length,
      banned: users.filter((u) => !u.active).length,
    };
  }, [users]);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageButtons = 10;
    const halfMax = Math.floor(maxPageButtons / 2);
    let startPage = Math.max(1, currentPage - halfMax);
    let endPage = Math.min(totalPages, currentPage + halfMax);

    if (currentPage <= halfMax) {
      endPage = Math.min(totalPages, maxPageButtons);
    }
    if (currentPage + halfMax > totalPages) {
      startPage = Math.max(1, totalPages - maxPageButtons + 1);
    }

    if (startPage > 1) {
      pageNumbers.push(
        <span key="ellipsis-start" className="px-2">
          ...
        </span>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => paginate(i)}
          className={`
            w-8 h-8 rounded-full text-sm font-medium mx-1 transition-colors duration-200
            ${
              currentPage === i
                ? "bg-blue-600 text-white shadow-md"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }
          `}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      pageNumbers.push(
        <span key="ellipsis-end" className="px-2">
          ...
        </span>
      );
    }

    return pageNumbers;
  };
  const renderModalContent = () => {
    if (!modalContent.action) return null;
    switch (modalContent.action) {
      case "edit_credits":
        return (
          <form
            id="edit-credits-form"
            onSubmit={(e) => handleFormAction(e, "edit_credits")}
          >
            <div className="mb-4">
              <label
                htmlFor="skipTrace"
                className="block text-sm font-medium text-gray-700 dark:text-gray-200"
              >
                Skiptrace Credits
              </label>
              <input
                type="number"
                id="skipTrace"
                value={skipTraceCredits}
                onChange={(e) => setSkipTraceCredits(e.target.value)}
                className="mt-1 block w-full bg-blue-100 p-2 border rounded-md dark:bg-gray-900 dark:text-gray-200"
              />
            </div>
            <div className="mb-6">
              <label
                htmlFor="autoQuery"
                className="block text-sm font-medium text-gray-700 dark:text-gray-200"
              >
                AI Query Credits
              </label>
              <input
                type="number"
                id="autoQuery"
                value={autoQueryCredits}
                onChange={(e) => setAutoQueryCredits(e.target.value)}
                className="mt-1 block w-full bg-blue-100 p-2 border rounded-md dark:bg-gray-900 dark:text-gray-200"
              />
            </div>
          </form>
        );
      case "ban":
        return <p className="block text-sm font-medium text-gray-700 mb-2 dark:bg-gray-800 dark:text-gray-200">Are you sure you want to ban {modalContent.user.name}?</p>;
      case "mass_notif":
        return (
          <form
            id="mass-notif-form"
            onSubmit={(e) => handleFormAction(e, "mass_notif")}
          >
            <div className="m-4">
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:bg-gray-800 dark:text-gray-200">
                Targeted Group:
              </label>
              <Select
                classNames={{
    menuButton: ({ isDisabled }) =>
      `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-indigo-500 focus:ring focus:ring-indigo-500/20 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200 ${
        isDisabled ? "bg-gray-200" : ""
      }`,
    menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200",
    listItem: ({ isSelected }) =>
      `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
        isSelected
          ? "text-white bg-indigo-500"
          : "text-gray-500 hover:bg-indigo-100 hover:text-indigo-500 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
      }`,
  }}
                value={notigroup}
                onChange={setNotigroup}
                options={[
                  { value: "00", label: "Big Dogs" },
                  { value: "01", label: "Lite Members" },
                ]}
              />
            </div>
            <div className="m-4">
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-200">
                Notification Message:
              </label>
              <textarea
                value={notimessage}
                onChange={(e) => setNotimessage(e.target.value)}
                rows="3"
                className="block p-2.5 w-full text-sm bg-blue-100 rounded-lg border dark:bg-gray-900 dark:text-gray-200"
              />
            </div>
            <div className="m-4">
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-200">
                URL (*Optional):
              </label>
              <input
                type="text"
                value={notiurl}
                onChange={(e) => setNotiurl(e.target.value)}
                className="block w-full px-3 py-2 bg-blue-100 border rounded-md dark:bg-gray-900 dark:text-gray-200"
              />
            </div>
          </form>
        );
      case "sync_partner":
        return (
          <form
            id="sync-partner-form"
            onSubmit={(e) => handleFormAction(e, "sync_partner")}
          >
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                Primary Member:
              </label>
              <input
                type="text"
                readOnly
                value={modalContent.user.email}
                className="w-full p-2 border rounded-md bg-gray-200 dark:bg-gray-900 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                Secondary Member:
              </label>
              <SelectAllUserEmails

                name="partner"
                value={selectedPartner}
                onChange={(name, value) => setSelectedPartner(value)}
                userEmails={users.map((u) => ({
                  value: u.email,
                  label: u.email,
                }))}
              />
            </div>
          </form>
        );
      case "sync_password":
        return (
          <p className="block text-sm font-medium text-gray-700 mb-2 dark:bg-gray-800 dark:text-gray-200">
            Are you sure you want to sync the password for{" "}
            {modalContent.user.name} with Keap?
          </p>
        );
      default:
        return <p>{modalContent.body || "Default modal content."}</p>;
    }
  };

  const renderModalFooter = () => {
    if (!modalContent.action) return null;
    let button;
    switch (modalContent.action) {
      case "edit_credits":
        button = (
          <button
            type="submit"
            form="edit-credits-form"
            disabled={actionLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:hover:bg-gray-800 dark:bg-gray-900 dark:text-gray-200 dark:border dark:border-gray-700"
          >
            {actionLoading ? "Saving..." : "Save Changes"}
          </button>
        );
        break;
      case "ban":
        button = (
          <button
            onClick={(e) => handleFormAction(e, "ban")}
            disabled={actionLoading}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 dark:hover:bg-gray-800 dark:bg-gray-900 dark:text-gray-200 dark:border dark:border-gray-700"
          >
            {actionLoading ? "Banning..." : "Confirm Ban"}
          </button>
        );
        break;
      case "mass_notif":
        button = (
          <button
            type="submit"
            form="mass-notif-form"
            disabled={actionLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:hover:bg-gray-800 dark:bg-gray-900 dark:text-gray-200 dark:border dark:border-gray-700"
          >
            {actionLoading ? "Sending..." : "Send Notification"}
          </button>
        );
        break;
      case "sync_partner":
        button = (
          <button
            type="submit"
            form="sync-partner-form"
            disabled={actionLoading || !selectedPartner}
            className="px-4 py-2 bg-blue-600 text-white rounded-md dark:hover:bg-gray-800 hover:bg-blue-700 dark:bg-gray-900 dark:text-gray-200 dark:border dark:border-gray-700"
          >
            {actionLoading ? "Syncing..." : "Confirm Sync"}
          </button>
        );
        break;
      case "sync_password":
        button = (
          <button
            onClick={(e) => handleFormAction(e, "sync_password")}
            disabled={actionLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md dark:hover:bg-gray-800 hover:bg-blue-700 dark:bg-gray-900 dark:text-gray-200 dark:border dark:border-gray-700"
          >
            {actionLoading ? "Syncing..." : "Synchronize"}
          </button>
        );
        break;
      default:
        return null;
    }
    return <>{button}</>;
  };

  return (
    <PageContent title="Admin" error={error}>
      <div className="p-6 space-y-6">
        {/* Admin Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Members"
            value={adminStats.total}
            icon={
              <svg
                className="w-6 h-6 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.653-.125-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.653.125-1.283.356-1.857m0 0a3.001 3.001 0 015.288 0M12 14a4 4 0 100-8 4 4 0 000 8z"
                />
              </svg>
            }
            accent="bg-blue-100 dark:bg-blue-900/30"
          />
          <StatCard
            title="Active Members"
            value={adminStats.active}
            icon={
              <svg
                className="w-6 h-6 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            }
            accent="bg-green-100 dark:bg-green-900/30"
          />
          <StatCard
            title="Big Dog Members"
            value={adminStats.bigDog}
            icon={
              <svg
                className="w-6 h-6 text-orange-600 dark:text-orange-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
            }
            accent="bg-orange-100 dark:bg-orange-900/30"
          />
          <StatCard
            title="Banned Members"
            value={adminStats.banned}
            icon={
              <svg
                className="w-6 h-6 text-red-600 dark:text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                />
              </svg>
            }
            accent="bg-red-100 dark:bg-red-900/30"
          />
        </div>

        {/* Table Card */}
        <div className="relative shadow-xl border-lg rounded-2xl bg-white p-4 dark:bg-gray-800 dark:text-gray-200 dark:border dark:border-gray-700">
        <div className="p-4 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50 rounded-t-2xl dark:bg-gray-800 dark:text-gray-200">
          <div className="flex w-full sm:w-3/4 gap-2">
            <div className="relative flex-1">
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by ID, Name, or Email"
                className="block w-full pl-10 p-2 border rounded-md dark:bg-gray-900 dark:text-gray-200 dark:placeholder-gray-400"
              />
            </div>
            {/* NEW: Filter Dropdown */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="block w-1/3 p-2 border rounded-md dark:bg-gray-900 dark:text-gray-200"
            >
              <option value="All">All Users</option>
              <option value="Big Dogs">Big Dogs</option>
              <option value="Non-Big Dogs">Non-Big Dogs</option>
            </select>
          </div>

          <button
            onClick={() => handleModalAction("mass_notif", user)}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded-lg border border-blue-500 text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <svg
              className="w-6 h-6 text-gray-800 dark:text-white"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                d="M7.556 8.5h8m-8 3.5H12m7.111-7H4.89a.896.896 0 0 0-.629.256.868.868 0 0 0-.26.619v9.25c0 .232.094.455.26.619A.896.896 0 0 0 4.89 16H9l3 4 3-4h4.111a.896.896 0 0 0 .629-.256.868.868 0 0 0 .26-.619v-9.25a.868.868 0 0 0-.26-.619.896.896 0 0 0-.63-.256Z"
              />
            </svg>

            <span>Notifications</span>
          </button>
        </div>
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-800 dark:text-gray-200">
            <tr>
              <th className="px-6 py-3">ID</th>
              <th className="px-6 py-3 hidden 2xl:table-cell">Name</th>
              <th className="px-6 py-3">Email</th>
              <th className="px-6 py-3">AI Credits</th>
              <th className="px-6 py-3">Skip Credits</th>
              <th className="px-6 py-3 hidden 2xl:table-cell">Leads</th>
              <th className="px-6 py-3 hidden 2xl:table-cell">Date Joined</th>
              <th className="px-6 py-3 hidden 2xl:table-cell">Active</th>
              <th className="px-6 py-3 hidden 2xl:table-cell">Keap ID</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.length > 0 ? (
              currentUsers.map((u, index) => (
                <tr
                  key={u.id}
                  className="bg-white border-b hover:bg-gray-50 dark:hover:bg-gray-900 dark:bg-gray-800 dark:text-gray-200"
                  ref={(el) => (menuRef.current[index] = el)}
                >
                  <td className="px-6 py-4">{u.id}</td>
                  <td className="px-6 py-4 hidden 2xl:table-cell">
                    {u.name || "N/A"}
                  </td>
                  <td className="px-6 py-4">{u.email}</td>
                  <td className="px-6 py-4">{u.query_credits}</td>
                  <td className="px-6 py-4">{u.skip_trace_credits}</td>
                  <td className="px-6 py-4 hidden 2xl:table-cell">{u.leads}</td>
                  <td className="px-6 py-4 hidden 2xl:table-cell">
                    {u.date_joined}
                  </td>
                  <td className="px-6 py-4 hidden 2xl:table-cell">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        u.active
                          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                      }`}
                    >
                      {u.active ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="px-6 py-4 hidden 2xl:table-cell">
                    {u.bigdog}
                  </td>
                  <td className="px-6 py-4 text-right relative">
                    <button
                      onClick={() =>
                        setMenuOpenForUser(
                          menuOpenForUser === u.id ? null : u.id
                        )
                      }
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Actions"
                    >
                      <svg
                        className="w-5 h-5 text-gray-500 dark:text-gray-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                      </svg>
                    </button>
                    {menuOpenForUser === u.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white border rounded shadow-lg z-10 dark:bg-gray-900 dark:hover:bg-gray-800 dark:text-gray-200">
                        <button
                          onClick={() => handleModalAction("edit_credits", u)}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:bg-gray-900 dark:hover:bg-gray-800 dark:text-gray-200"
                        >
                          Edit Credits
                        </button>
                        <button
                          onClick={() => handleModalAction("ban", u)}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:bg-gray-900 dark:hover:bg-gray-800 dark:text-gray-200"
                        >
                          {u.active ? "Ban User" : "Unban User"}
                        </button>
                        <button
                          onClick={() => handleModalAction("sync_password", u)}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:bg-gray-900 dark:hover:bg-gray-800 dark:text-gray-200"
                        >
                          Sync Password to Keap
                        </button>
                        <button
                          onClick={() => handleModalAction("sync_partner", u)}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:bg-gray-900 dark:hover:bg-gray-800 dark:text-gray-200"
                        >
                          Sync with Partner
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="10"
                  className="px-6 py-4 text-center text-gray-500"
                >
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {filteredUsers.length > itemsPerPage && (
          <div className="p-4 flex items-center justify-between border-t border-gray-200 bg-gray-50 rounded-b-2xl dark:bg-gray-800 dark:text-gray-200">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <div className="flex-grow flex justify-center">
              {renderPageNumbers()}
            </div>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
        </div>
      </div>
      <Modal
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalContent.title || "Admin Action"}
        footer={renderModalFooter()}
        icon={getModalIconAndColor(modalContent.action).icon}
        iconColor={getModalIconAndColor(modalContent.action).color}
      >
        {renderModalContent()}
      </Modal>
    </PageContent>
  );
};

export default Admin;